import { app } from '../../../../../lib/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { isStaging } from '../../../../../config/environment';

/**
 * Converts and compresses an image file to WebP format
 * @param file Original image file
 * @returns Promise with the compressed WebP blob
 */
const convertToWebP = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      const maxDimension = 1920; // Max dimension for website images

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      // Set canvas size to the new dimensions
      canvas.width = width;
      canvas.height = height;

      // Enable image smoothing for better quality
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      }

      // Clear canvas and draw image
      ctx?.clearRect(0, 0, width, height);
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert to WebP with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert image to WebP'));
          }
        },
        'image/webp',
        0.85 // Quality level for WebP (0-1)
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Uploads an image to Firebase Storage and returns the download URL
 * Uses speculo-onboarding-prod or speculo-onboarding-staging buckets
 * @param file - The image file to upload
 * @param organizationId - The organization ID for path organization
 * @param type - Type of image (e.g., 'hero-background', 'logo', etc.)
 * @returns Promise<string> - The download URL of the uploaded image
 */
export const uploadImageToStorage = async (
  file: File,
  organizationId: string,
  type: string
): Promise<string> => {
  try {
    // Check if it's a video file - if so, upload directly without conversion
    if (file.type.startsWith('video/')) {
      return uploadVideoToStorage(file, organizationId, type);
    }

    // Convert and compress image to WebP
    const webpBlob = await convertToWebP(file);

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const cleanName = file.name
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, '-')
      .replace(/-+/g, '-')
      .replace(/\.[^.]+$/, ''); // Remove original extension

    const fileName = `${organizationId}/${type}_${timestamp}_${cleanName}.webp`;

    // Use appropriate storage bucket based on environment
    // Staging: speculo-onboarding/website-builder/
    // Production: heyisa-media/speculo-onboarding/website-builder/
    const storage = getStorage(app);
    const storagePath = isStaging()
      ? `speculo-onboarding/website-builder/${fileName}`
      : `heyisa-media/speculo-onboarding/website-builder/${fileName}`;
    const storageRef = ref(storage, storagePath);

    // Upload the file
    await uploadBytes(storageRef, webpBlob, {
      contentType: 'image/webp',
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        organizationId,
        type,
      },
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      file: file.name,
      orgId: organizationId,
      type,
      isStaging: isStaging(),
    });
    throw new Error(
      `Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Uploads a video to Firebase Storage and returns the download URL
 * Videos are uploaded directly without conversion (to maintain quality)
 * @param file - The video file to upload
 * @param organizationId - The organization ID for path organization
 * @param type - Type of video (e.g., 'hero-video', 'team-video', etc.)
 * @returns Promise<string> - The download URL of the uploaded video
 */
export const uploadVideoToStorage = async (
  file: File,
  organizationId: string,
  type: string
): Promise<string> => {
  try {
    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const cleanName = file.name
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, '-')
      .replace(/-+/g, '-');

    // Get the file extension
    const extension = file.name.split('.').pop()?.toLowerCase() || 'mp4';
    const baseNameWithoutExt = cleanName.replace(/\.[^.]+$/, '');

    const fileName = `${organizationId}/${type}_${timestamp}_${baseNameWithoutExt}.${extension}`;

    // Use appropriate storage bucket based on environment
    // Staging: speculo-onboarding/website-builder/
    // Production: heyisa-media/speculo-onboarding/website-builder/
    const storage = getStorage(app);
    const storagePath = isStaging()
      ? `speculo-onboarding/website-builder/${fileName}`
      : `heyisa-media/speculo-onboarding/website-builder/${fileName}`;
    const storageRef = ref(storage, storagePath);

    // Upload the video file directly (no conversion)
    await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        organizationId,
        type,
        mediaType: 'video',
      },
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading video:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      file: file.name,
      orgId: organizationId,
      type,
      isStaging: isStaging(),
    });
    throw new Error(
      `Failed to upload video: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Deletes an image from Firebase Storage given its download URL
 * @param imageUrl - The download URL of the image to delete
 * @returns Promise<void>
 */
export const deleteImageFromStorage = async (imageUrl: string): Promise<void> => {
  if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
    // Skip deletion for preview URLs or data URLs
    return;
  }

  try {
    const storage = getStorage(app);

    // Extract the storage path from the download URL
    // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
    const urlParts = imageUrl.split('/o/');
    if (urlParts.length < 2) {
      console.warn('Invalid Firebase Storage URL format:', imageUrl);
      return;
    }

    const pathWithToken = urlParts[1];
    const storagePath = decodeURIComponent(pathWithToken.split('?')[0]);

    const imageRef = ref(storage, storagePath);
    await deleteObject(imageRef);

    console.log('Successfully deleted image:', storagePath);
  } catch (error) {
    // Log error but don't throw - deletion failures shouldn't block the upload
    console.error('Error deleting old image:', error);
    console.error('Image URL:', imageUrl);
  }
};
